import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../api';
import { ar, en } from '../translations';

const AppContext = createContext(null);

const GCC_COUNTRIES = {
  KW: { name_en: 'Kuwait',       name_ar: 'الكويت',   currency_code: 'KWD', currency_symbol: 'د.ك', rate_from_kwd: 1.0,   buying_enabled: true },
  SA: { name_en: 'Saudi Arabia', name_ar: 'السعودية', currency_code: 'SAR', currency_symbol: 'ر.س', rate_from_kwd: 12.2,  buying_enabled: true },
  AE: { name_en: 'UAE',          name_ar: 'الإمارات', currency_code: 'AED', currency_symbol: 'د.إ', rate_from_kwd: 11.96, buying_enabled: true },
  BH: { name_en: 'Bahrain',      name_ar: 'البحرين',  currency_code: 'BHD', currency_symbol: 'د.ب', rate_from_kwd: 1.3,   buying_enabled: true },
  QA: { name_en: 'Qatar',        name_ar: 'قطر',      currency_code: 'QAR', currency_symbol: 'ر.ق', rate_from_kwd: 11.84, buying_enabled: true },
  OM: { name_en: 'Oman',         name_ar: 'عمان',     currency_code: 'OMR', currency_symbol: 'ر.ع', rate_from_kwd: 1.25,  buying_enabled: true },
  OTHER: { name_en: 'Other',     name_ar: 'أخرى',     currency_code: 'USD', currency_symbol: '$',    rate_from_kwd: 3.26,  buying_enabled: false },
};

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('sf_lang') || 'ar');
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem('sf_country') || '');
  const [countries, setCountries] = useState(GCC_COUNTRIES);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sf_cart') || '[]'); } catch { return []; }
  });

  const t = lang === 'ar' ? ar : en;

  useEffect(() => {
    api.getSettings().then(res => {
      if (res.data.countries) setCountries(res.data.countries);
      if (res.data.whatsapp_number) setWhatsappNumber(res.data.whatsapp_number);
    }).catch(() => {});

    if (!countryCode) {
      fetch('https://ipapi.co/json/')
        .then(r => r.json())
        .then(d => {
          const code = d.country_code || 'OTHER';
          const resolved = GCC_COUNTRIES[code] ? code : 'OTHER';
          setCountryCode(resolved);
          localStorage.setItem('sf_country', resolved);
        })
        .catch(() => { setCountryCode('OTHER'); });
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('sf_lang', lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  useEffect(() => {
    localStorage.setItem('sf_cart', JSON.stringify(cart));
  }, [cart]);

  const currentCountry = countries[countryCode] || countries['OTHER'] || GCC_COUNTRIES['OTHER'];

  const formatPrice = (kwd) => {
    if (!kwd) return '';
    const converted = kwd * (currentCountry.rate_from_kwd || 1);
    return `${converted.toFixed(3)} ${currentCountry.currency_symbol}`;
  };

  const addToCart = (sheep) => {
    setCart(prev => {
      if (prev.find(i => i.id === sheep.id)) return prev;
      return [...prev, sheep];
    });
  };

  const removeFromCart = (id) => setCart(prev => prev.filter(i => i.id !== id));
  const clearCart = () => setCart([]);
  const isInCart = (id) => cart.some(i => i.id === id);

  const cartTotal = cart.reduce((sum, i) => sum + (i.online_price || 0), 0);

  return (
    <AppContext.Provider value={{
      lang, setLang, t,
      countryCode, setCountryCode, countries, currentCountry,
      whatsappNumber,
      formatPrice,
      cart, addToCart, removeFromCart, clearCart, isInCart, cartTotal,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
